/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef } from 'react';

import P from 'prop-types';
import Carousel from '@nerdwallet/react-carousel';
import classNames from 'classnames';

import { CenteredContainer, Box, Button } from '@nerdwallet/currency';
import Right from '@nerdwallet/react-icon/gizmo/Right';

import { useMediaQuery } from '~/app/lib/use-media-query';
import { useDispatch } from 'react-redux';

import { COMPONENT_UPDATED } from '~/app/lib/redux/actions/action-types';
import { useBuilder } from '../../lib/redux/selectors/builder-selector';
import getTextColor from '../../lib/text-color-config';

import FlexCardItem from './flex-card-item/flex-card-item';
import { CARDS_PER_ROW_ENUM, CARD_TREATMENT_ENUM } from './lib/constants';
import getEditingConfig from './lib/editing-config';

import UniversalContainer from '../universal-container';
import UniversalText from '../universal-text/universal-text';
import { defaultProps } from './lib/default-options';

import './flex-card.scss';
import ArticleCard from './article-card/article-card';
import useArticles from './article-card/use-articles';

const updateComponent = (component) => ({
  type: COMPONENT_UPDATED,
  component,
});

const setRowStyle = (cardsPerRow, cards, size) => {
  const total = Math.ceil(cards.length / parseInt(cardsPerRow, 10));
  if (size === 'md' && cardsPerRow === '4') {
    return {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateRows: `repeat(${Math.ceil(cards.length / 3)}, 1fr)`,
    };
  }
  if (size === 'sm') {
    return {
      gridTemplateColumns: `repeat(1, 1fr)`,
      gridTemplateRows: `repeat(${cards.length}, 1fr)`,
    };
  }
  return {
    gridTemplateColumns: `repeat(${cardsPerRow}, 1fr)`,
    gridTemplateRows: `repeat(${total}, 1fr)`,
  };
};

const NextSlide = ({ onClick, arrowColor, className }) => {
  const classStyle = className.replace('slick-arrow slick-next', '');

  return (
    <Button
      className={classNames([classStyle, 'flex-card-list-carousel-arrow'])}
      tertiary
      onClick={onClick}
    >
      <Right size={30} color={arrowColor} />
    </Button>
  );
};

const FlexCards = ({
  container,
  title,
  subtitle,
  arrowColor,
  stacked,
  cardsPerRow,
  desktopCarousel,
  cardsToPreview,
  cards,
  wideSizeLayout,
  carouselDotColorOptions,
  carouselInactiveDotColorOptions,
  carouselArrowColorOptions,
  carouselArrowBackgroundOptions,
  displayMobileCarouselArrow,
  arrowsOnTheEdges,
  disableDefaultProps,
  untouched,
  componentId,
  isCopy,
  useArticlesData,
  tablePressId,
}) => {
  const articleCards = useArticles({ useArticlesData, tablePressId });
  const { textColor: carouselDotColor } = getTextColor(carouselDotColorOptions);
  const { textColor: carouselInactiveDotColor } = getTextColor(
    carouselInactiveDotColorOptions
  );
  const { textColor: carouselArrowColor } = getTextColor(
    carouselArrowColorOptions
  );
  const { textColor: carouselArrowBackgroundColor } = getTextColor(
    carouselArrowBackgroundOptions
  );
  const size = useMediaQuery();
  const flexCardContainerRef = useRef();
  const flexCardsWithEmptyPropsRef = useRef();
  const { selected, flatOutline } = useBuilder();
  const dispatch = useDispatch();
  const isMobileCarousel = size === 'sm' && !stacked;
  const isDesktopCarousel = desktopCarousel && size !== 'sm';
  const selectedComponent = flatOutline?.[selected];

  // saves the very initial version of the FlexCard
  // to be used when "disableDefaultProps" is activated
  useEffect(() => {
    if (untouched && !flexCardsWithEmptyPropsRef.current) {
      flexCardsWithEmptyPropsRef.current = selectedComponent;
    }
  }, [selectedComponent, untouched]);

  // Decides whether or not if loads the base template
  useEffect(() => {
    if (
      untouched &&
      selectedComponent?.id === componentId &&
      !disableDefaultProps &&
      !isCopy
    ) {
      dispatch(
        updateComponent({
          ...selectedComponent,
          props: { ...defaultProps, untouched: false },
        })
      );
    }
  }, [
    componentId,
    disableDefaultProps,
    selectedComponent,
    untouched,
    isCopy,
    dispatch,
  ]);

  // If "disableDefaultProps" is active it load the very initial version of FlexCard
  useEffect(() => {
    if (disableDefaultProps) {
      dispatch(
        updateComponent({
          ...flexCardsWithEmptyPropsRef.current,
          props: {
            ...flexCardsWithEmptyPropsRef.current?.props,
            disableDefaultProps: true,
          },
        })
      );
    }
  }, [disableDefaultProps, dispatch]);

  const mobileStackedOrWide =
    (size !== 'sm' && !desktopCarousel) || (size === 'sm' && stacked);

  const widerCarousel = wideSizeLayout && isDesktopCarousel;

  useEffect(() => {
    if (flexCardContainerRef.current) {
      const dotList =
        flexCardContainerRef.current.querySelector('ul.slick-dots');

      if (dotList && carouselDotColor) {
        dotList.classList.add('custom-dot-color');
        dotList.style.setProperty('--activeDot', carouselInactiveDotColor);
        dotList
          .querySelectorAll('.flex-card-list-dots li button')
          .forEach((button) => {
            button.style.color = carouselDotColor;
          });
      }

      const slickArrows =
        flexCardContainerRef.current.querySelectorAll('button.slick-arrow');

      if (slickArrows.length) {
        slickArrows.forEach((arrow) => {
          if (carouselArrowBackgroundColor) {
            arrow.style.backgroundColor = carouselArrowBackgroundColor;
          }
          if (carouselArrowColor) {
            arrow.style.color = carouselArrowColor;
          }
        });
      }
    }
  }, [
    carouselArrowBackgroundColor,
    carouselArrowColor,
    carouselDotColor,
    carouselInactiveDotColor,
    displayMobileCarouselArrow,
    isDesktopCarousel,
    widerCarousel,
    size,
  ]);
  const rowStyle = setRowStyle(
    cardsPerRow,
    useArticlesData ? articleCards : cards,
    size
  );

  return (
    <Box ref={flexCardContainerRef}>
      <UniversalContainer {...container}>
        <CenteredContainer
          className={classNames([
            'flex-cards-wrapper',
            {
              carousel: (size === 'sm' && !stacked) || desktopCarousel,
              'oversize-layout': !wideSizeLayout || isDesktopCarousel,
              'no-arrow': !displayMobileCarouselArrow,
              'arrows-on-the-edges': arrowsOnTheEdges,
              widerCarousel,
            },
          ])}
        >
          {title?.text && <UniversalText {...title} />}

          {subtitle?.text && (
            <UniversalText
              {...subtitle}
              defaultOptions={{ text: { textType: 'text' } }}
            />
          )}

          {mobileStackedOrWide && (
            <Box style={rowStyle} className="flex-cards-list-container">
              {articleCards.isReady
                ? articleCards.articles.map((article, idx) => (
                    <ArticleCard key={`article_${idx}`} article={article} />
                  ))
                : cards.map((item, index) => (
                    <FlexCardItem key={item.$unique_key || index} {...item} />
                  ))}
            </Box>
          )}

          {isMobileCarousel && (
            <Carousel
              id="nerd-carousel"
              autoplay={false}
              allowLooping
              decorateNav={false}
              enableDotNavigation
              dots
              nextArrow={
                displayMobileCarouselArrow ? (
                  <NextSlide arrowColor={arrowColor} />
                ) : null
              }
              dotsClass="slick-dots flex-card-list-dots"
              slidesToShow={useArticlesData ? '1.5' : '1.15'}
              className="flex-cards-list-container"
            >
              {articleCards.isReady
                ? articleCards.articles.map((article, idx) => (
                    <ArticleCard key={`article_${idx}`} article={article} />
                  ))
                : cards.map((item, index) => (
                    <FlexCardItem key={item.$unique_key || index} {...item} />
                  ))}
            </Carousel>
          )}

          {isDesktopCarousel && !wideSizeLayout && (
            <Carousel
              id="nerd-carousel"
              autoplay={false}
              allowLooping
              decorateNav={false}
              enableDotNavigation
              dots
              dotsClass="slick-dots flex-card-list-dots"
              slidesToShow={cardsToPreview || '3'}
              className="flex-cards-list-container"
            >
              {articleCards.isReady
                ? articleCards.articles.map((article, idx) => (
                    <ArticleCard key={`article_${idx}`} article={article} />
                  ))
                : cards.map((item, index) => (
                    <FlexCardItem
                      key={item.$unique_key || index}
                      desktopCarousel={desktopCarousel}
                      cardsPerRow={cardsToPreview}
                      {...item}
                    />
                  ))}
            </Carousel>
          )}
          {widerCarousel && (
            <CenteredContainer>
              <Carousel
                id="nerd-carousel"
                autoplay={false}
                allowLooping
                decorateNav={false}
                enableDotNavigation
                dots
                dotsClass="slick-dots flex-card-list-dots"
                slidesToShow={cardsToPreview || '3'}
                className="flex-cards-list-container"
              >
                {articleCards.isReady
                  ? articleCards.articles.map((article, idx) => (
                      <ArticleCard key={`article_${idx}`} article={article} />
                    ))
                  : cards.map((item, index) => (
                      <FlexCardItem
                        key={item.$unique_key || index}
                        desktopCarousel={desktopCarousel}
                        cardsPerRow={cardsToPreview}
                        {...item}
                      />
                    ))}
              </Carousel>
            </CenteredContainer>
          )}
        </CenteredContainer>
      </UniversalContainer>
    </Box>
  );
};

FlexCards.getEditingConfig = getEditingConfig;

FlexCards.displayName = 'FlexCards';

export default FlexCards;

NextSlide.propTypes = {
  className: P.string,
  onClick: P.func,
  arrowColor: P.string,
};

FlexCards.propTypes = {
  cards: P.array,
  stacked: P.bool,
  cardsPerRow: P.string,
  desktopCarousel: P.bool,
  cardTreatment: P.string,
  borderColorTreatment: P.string,
  borderSizeTreatment: P.string,
  shadowColorTreatment: P.string,
  arrowColor: P.string,
  wideSizeLayout: P.bool,
  cardsToPreview: P.string,
  displayMobileCarouselArrow: P.bool,
  arrowsOnTheEdges: P.bool,
  carouselDotColorOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  carouselInactiveDotColorOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  carouselArrowColorOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  carouselArrowBackgroundOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  container: P.object,
  title: P.object,
  subtitle: P.object,
  children: P.node,
  disableDefaultProps: P.bool,
  selectedComponent: P.object,
  componentId: P.string,
  untouched: P.bool,
  flatOutline: P.object,
  updateComponentProps: P.func,
  isCopy: P.bool,
  titleSize: P.string,
  useArticlesData: P.bool,
  tablePressId: P.number,
};

FlexCards.defaultProps = {
  cards: [],
  cardsPerRow: CARDS_PER_ROW_ENUM.THREE,
  cardsToPreview: CARDS_PER_ROW_ENUM.THREE,
  stacked: false,
  cardTreatment: CARD_TREATMENT_ENUM.NONE,
  shadowColorTreatment: '#cdcdcd',
  borderColorTreatment: '#cdcdcd',
  borderSizeTreatment: '1px',
  titleSize: 'x-large',
  arrowColor: '#008255',
  displayMobileCarouselArrow: false,
  arrowsOnTheEdges: false,
  carouselDotColorOptions: {
    hexColor: '#008255',
  },
  carouselInactiveDotColorOptions: {
    hexColor: '#cdcdcd',
  },
  disableDefaultProps: false,
  children: null,
  useArticlesData: false,
  tablePressId: 2944,
};
